import { Link, Outlet } from 'react-router'
import React, { JSX, useState } from 'react'
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  TransitionChild
} from '@headlessui/react'
import {
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline'
import { AnalyticsUser, CLIENT_STORAGE_KEY, useAuth } from 'context/user'
import { LoadingPage } from 'components/Loading'
import { useAuth0 } from '@auth0/auth0-react'
import { ROOT_PATH } from 'components/Routing'
import { GrooveWidget } from './GrooveWidget'
import { FILTERS_STORAGE_KEY } from 'hooks/usePersistentAnalyticsFilters'

export interface NavigationItem {
  name: string
  icon: React.ForwardRefExoticComponent<Omit<React.SVGProps<SVGSVGElement>, 'ref'> & {
    title?: string
    titleId?: string
  } & React.RefAttributes<SVGSVGElement>>
  href: string
}

const Sidebar: React.FC<{
  navigation: NavigationItem[]
  open: boolean
  onClose: () => void
  user: AnalyticsUser
  onLogout: () => void
}> = ({ navigation, open, onClose, user, onLogout }) => (

  <Dialog open={open} onClose={onClose} className='relative z-50'>
    <DialogBackdrop transition className='fixed inset-0 bg-lm-bg-primary/80 dark:bg-dm-bg-primary/80 transition-opacity duration-300 ease-linear data-closed:opacity-0' />
    <div className='fixed inset-0 flex'>
      <DialogPanel transition className='relative mr-16 flex w-full max-w-xs flex-1 transform transition duration-300 ease-in-out data-closed:-translate-x-full'>
        <TransitionChild>
          <div className='absolute top-0 left-full flex w-16 justify-center pt-5 duration-300 ease-in-out data-closed:opacity-0'>
            <button onClick={onClose} className='-m-2.5 p-2.5'>
              <span className='sr-only'>Close sidebar</span>
              <XMarkIcon className='size-6 text-white dark:text-gray-100' aria-hidden='true' />
            </button>
          </div>
        </TransitionChild>

        <div className='flex grow flex-col gap-y-5 overflow-y-auto bg-black px-6 pb-2'>
          <div className='flex h-16 shrink-0 items-center justify-center'>
            <img alt='StagePilot' src='/images/logo-letters.png' className='h-8 w-auto' />
          </div>
          <nav className='flex flex-1 flex-col justify-between'>
            <ul className='-mx-2 space-y-1'>
              {navigation.map(({ name, href, icon: Icon }) => (
                <li key={name}>
                  <Link
                    to={href}
                    className='bg-lm-bg-primary dark:bg-dm-bg-primary group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-lm-text-accent dark:text-dm-text-accent'
                  >
                    <Icon className='size-6 shrink-0 text-lm-text-accent dark:text-dm-text-accent' aria-hidden='true' />
                    {name}
                  </Link>
                </li>
              ))}
            </ul>

            <div className='border-t border-text-lm-text-accent dark:text-dm-text-accent pt-4 pb-3'>
              <div className='flex items-center'>
                <div className='ml-3'>
                  <div data-testid='analytics-user-name' className='text-base font-medium text-lm-text-accent dark:text-dm-text-accent'>
                    {user.name}
                  </div>
                  <div data-testid='analytics-user-email' className='text-sm font-medium text-lm-text-secondary text-dm-text-secondary'>
                    {user.email}
                  </div>
                </div>
              </div>
              <div className='mt-3 space-y-1'>
                <button
                  data-testid='sign-out-button'
                  onClick={onLogout}
                  className='block w-full text-left px-4 py-2 text-base rounded-md font-medium text-lm-text-accent dark:text-dm-text-accent hover:bg-lm-bg-primary/80 dark:hover:bg-dm-bg-primary/80'
                >
                  Sign out
                </button>
              </div>
            </div>
          </nav>
        </div>
      </DialogPanel>
    </div>
  </Dialog>
)

const Layout = (): JSX.Element => {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const { analyticsUser, auth0Token } = useAuth()
  const { logout } = useAuth0()

  const navigation = [
    { name: 'Overview', icon: ChartBarIcon, href: ROOT_PATH }
  ]

  const sideBarButton = (
    <button
      data-testid='open-sidebar-button'
      onClick={() => setSidebarOpen(true)}
      className='flex items-center text-lm-text-accent dark:text-dm-text-accent'
    >
      <Bars3Icon className='size-4' aria-hidden='true' />
      <span className='sr-only'>Open sidebar</span>
    </button>
  )

  const handleLogout = (): void => {
    globalThis.localStorage.removeItem(FILTERS_STORAGE_KEY)
    globalThis.localStorage.removeItem(CLIENT_STORAGE_KEY)
    void logout({ logoutParams: { returnTo: window.location.origin } })
  }

  const loadedUserToken = (analyticsUser !== null && auth0Token !== null)
  const content = ((analyticsUser === null) || auth0Token === null) ? <LoadingPage /> : <Outlet />

  return (
    <div data-testid='layout-wrapper'>
      <GrooveWidget ready={loadedUserToken} />
      {loadedUserToken &&
        <Sidebar
          navigation={navigation}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          user={analyticsUser}
          onLogout={handleLogout}
        />}

      <div className='sticky top-0 z-40 flex items-center bg-black px-4 py-4 shadow-xs dark:shadow-[0_12px_32px_-14px_rgba(0,0,0,0.7)] dark:ring-1 dark:ring-white/3 sm:px-6 min-h-12'>
        {loadedUserToken && sideBarButton}
        <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
          <img alt='StagePilot Logo' src='/images/sp-wordmark.png' className='w-auto h-4' />
        </div>
      </div>

      <main className='pb-[100px]'>
        {content}
      </main>
    </div>
  )
}

export default Layout
